import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../modules/auth/services/auth.service';
import { AuthservicesService } from '../../services/authservices.service';

@Component({
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, ReactiveFormsModule],
  selector: 'app-password-reset',
  templateUrl: './password-reset.component.html',
  styleUrls: ['./password-reset.component.scss'],
})
export class PasswordResetComponent implements OnInit, OnDestroy {
  private authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;
  public resetPasswordForm: FormGroup;
  public showSuccess: boolean = false;
  public showError: boolean = false;
  public errorMessage: string = '';
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;
  public isLoading: boolean = false;
  public redirectCountdown: number = 0;
  private countdownInterval: any;


  public passwordFeedback: string[] = [];
  public hasMinLength: boolean = false;
  public hasUppercase: boolean = false;
  public hasLowercase: boolean = false;
  public hasDigit: boolean = false;
  public hasSpecialChar: boolean = false;

  private token: string;
  public userId: string; // Make userId public so it can be accessed in template
  public hasQueryParams: boolean = false;
  public isAuthenticated: boolean = false;



  togglePasswordVisibility(field: 'password' | 'confirmPassword'): void {
    if (field === 'password') {
      this.showPassword = !this.showPassword;
    } else {
      this.showConfirmPassword = !this.showConfirmPassword;
    }
  }

  navigateToDashboard(): void {
    this.toastr.info('Redirecting to dashboard...', 'Please wait');
    this.startRedirectCountdown(3, '/pages/dashboard');
  }

  private startRedirectCountdown(seconds: number, route: string): void {
    this.redirectCountdown = seconds;
    this.countdownInterval = setInterval(() => {
      this.redirectCountdown--;
      if (this.redirectCountdown <= 0) {
        clearInterval(this.countdownInterval);
        this.routermove.navigate([route]);
      }
    }, 1000);
  }

  private clearRedirectCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    this.redirectCountdown = 0;
  }

  mustMatch(password: string, confirmPassword: string): any {
    return (formGroup: FormGroup) => {
      const passControl = formGroup.controls[password];
      const confirmPassControl = formGroup.controls[confirmPassword];

      if (confirmPassControl.errors && !confirmPassControl.errors['mustMatch']) {
        return;
      }

      if (passControl.value !== confirmPassControl.value) {
        confirmPassControl.setErrors({ mustMatch: true });
      } else {
        confirmPassControl.setErrors(null);
    }
  }
}
  constructor(
    private authService: AuthservicesService,
    private authSessionService: AuthService,
    private route: ActivatedRoute,
    private routermove: Router,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    const queryParams = this.route.snapshot.queryParams;
    this.hasQueryParams = Object.keys(queryParams).length > 0;

    // Check if user is authenticated via localStorage
    this.isAuthenticated = !!localStorage.getItem(this.authLocalStorageToken);

    // If no query params and not authenticated, redirect to login
    if (!this.hasQueryParams && !this.isAuthenticated) {
      this.toastr.warning('Please log in to change your password', 'Authentication Required');
      this.routermove.navigate(['/auth']);
      return;
    }

    // Initialize form with appropriate fields based on whether user has query params
    if (this.hasQueryParams) {
      this.resetPasswordForm = new FormGroup({
        password: new FormControl('', [
          Validators.required,
          Validators.minLength(6),
        ]),
        confirmPassword: new FormControl('', [Validators.required]),
      }, { validators: this.mustMatch('password', 'confirmPassword') });
    } else {
      // For logged-in users, include oldPassword field
      this.resetPasswordForm = new FormGroup({
        oldPassword: new FormControl('', [Validators.required]),
        password: new FormControl('', [
          Validators.required,
          Validators.minLength(6),
        ]),
        confirmPassword: new FormControl('', [Validators.required]),
      }, { validators: this.mustMatch('password', 'confirmPassword') });
    }
    
    this.showPassword = false;

    this.token = this.route.snapshot.queryParams['token'] || this.route.snapshot.queryParams['code'];
    this.userId = this.route.snapshot.queryParams['userId'];

    // Check if token exists when required
    if (this.hasQueryParams && !this.token) {
      this.toastr.error('Invalid password reset link', 'Error');
      this.routermove.navigate(['/auth']);
      return;
    }

    // Check if userId exists when required
    if (this.hasQueryParams && !this.userId) {
      this.toastr.error('Invalid password reset link - User ID missing', 'Error');
      this.routermove.navigate(['/auth']);
      return;
    }
  }

  checkPassword(): void {
    const password = this.resetPasswordForm.get('password')?.value;
    this.passwordFeedback = [];
    this.hasMinLength = password.length >= 10;
    this.hasUppercase = /[A-Z]/.test(password);
    this.hasLowercase = /[a-z]/.test(password);
    this.hasDigit = /\d/.test(password);
    this.hasSpecialChar = /[!@#$%^&*]/.test(password);

    this.passwordFeedback = [];
    if (!this.hasMinLength) {
      this.passwordFeedback.push('Must be at least 10 alphanumeric characters or more');
    }
    if (!this.hasUppercase) {
      this.passwordFeedback.push('Must have at least one uppercase letter');
    }
    if (!this.hasLowercase) {
      this.passwordFeedback.push('Must have at least one lowercase letter');
    }
    if (!this.hasDigit) {
      this.passwordFeedback.push('Must have at least one digit');
    }
    if (!this.hasSpecialChar) {
      this.passwordFeedback.push('Must have at least one special character');
    }
  }

  // Method to mask email/userId for privacy
  getMaskedUserId(): string {
    if (!this.userId) return '';
    if (this.userId.includes('@')) {
      const [localPart, domain] = this.userId.split('@');
      const maskedLocal = localPart.length > 2 
        ? localPart.substring(0, 2) + '*'.repeat(localPart.length - 2)
        : '*'.repeat(localPart.length);
      return `${maskedLocal}@${domain}`;
    } else {
      if (this.userId.length <= 4) {
        return '*'.repeat(this.userId.length);
      } else {
        const start = this.userId.substring(0, 2);
        const end = this.userId.substring(this.userId.length - 2);
        const middle = '*'.repeat(this.userId.length - 4);
        return `${start}${middle}${end}`;
      }
    }
  }

  getpasswordcomponent(event: Event) {
    if (event) {
      event.preventDefault();
    }
  }

  public resetPassword(event: Event, resetPasswordFormValue: any): void {
    event.preventDefault();

    if (this.resetPasswordForm.invalid) {
      this.resetPasswordForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.showError = false;
    this.showSuccess = false;
    this.errorMessage = '';
    this.clearRedirectCountdown();

    if (this.hasQueryParams) {
      // Ensure token is properly extracted and trimmed
      const token = (this.route.snapshot.queryParams['code'] || this.route.snapshot.queryParams['token'] || '').trim();
      
      if (!token) {
        this.isLoading = false;
        this.showError = true;
        this.errorMessage = 'Invalid or missing reset token';
        this.toastr.error(this.errorMessage, 'Error!');
        return;
      }

      const resetPassDto = {
        Token: token,
        email: this.userId?.trim() || '',
        newPassword: resetPasswordFormValue.password?.trim() || '',
        confirmPassword: resetPasswordFormValue.confirmPassword?.trim() || '',
      };

      // Validate DTO before sending
      if (!resetPassDto.email) {
        this.isLoading = false;
        this.showError = true;
        this.errorMessage = 'Email address is required';
        this.toastr.error(this.errorMessage, 'Error!');
        return;
      }

      if (!resetPassDto.newPassword || !resetPassDto.confirmPassword) {
        this.isLoading = false;
        this.showError = true;
        this.errorMessage = 'Password fields are required';
        this.toastr.error(this.errorMessage, 'Error!');
        return;
      }

      if (resetPassDto.newPassword !== resetPassDto.confirmPassword) {
        this.isLoading = false;
        this.showError = true;
        this.errorMessage = 'Passwords do not match';
        this.toastr.error(this.errorMessage, 'Error!');
        return;
      }

     // console.log('Sending password reset request with email:', resetPassDto.email);

      this.authService.resetPassword(resetPassDto).subscribe({
        next: (_) => {
          console.log('Password reset success');
          this.showSuccess = true;
          this.showError = false;
          this.isLoading = false;
          
          // Reset password attempt counter on success
          this.authSessionService.resetPasswordAttemptCounter();
          
          this.toastr.success('Password reset successfully!', 'Success!');
          this.startRedirectCountdown(5, '/auth'); // 5 second countdown
        },
        error: (error) => {
          console.error('Password reset error:', error);
          console.error('Error status:', error.status);
          console.error('Error message:', error.error);
          
          this.isLoading = false;
          this.clearRedirectCountdown();
          this.showError = true;
          this.showSuccess = false;
          
          // Provide more specific error messages based on status code
          if (error.status === 400) {
            this.errorMessage = error.error?.message || error.error || 'Invalid request. Please check your password reset link and try again.';
          } else if (error.status === 401) {
            this.errorMessage = 'Your password reset link has expired. Please request a new one.';
          } else if (error.status === 404) {
            this.errorMessage = 'User not found. Please verify your email address.';
          } else {
            this.errorMessage = error.error?.message || error.error || 'An error occurred. Please try again.';
          }
          
          this.toastr.error(this.errorMessage, 'Error!');
          
          // Increment attempt counter - will auto-redirect after 3rd attempt
          this.authSessionService.incrementPasswordAttempt();
        }
      });
    } else {
      const changePasswordDto = {
        OldPassword: resetPasswordFormValue.oldPassword,
        Password: resetPasswordFormValue.password,
        ConfirmPassword: resetPasswordFormValue.confirmPassword
      };

      this.authService.resetnewPassword(changePasswordDto).subscribe({
        next: (_) => {
          console.log('Password change success - setting showSuccess = true');
          this.showSuccess = true;
          this.showError = false;
          this.isLoading = false;
          
          // Reset password attempt counter on success
          this.authSessionService.resetPasswordAttemptCounter();
          
          this.toastr.success('Password changed successfully! Logging you out...', 'Success!');
          
          // Clear localStorage since password changed
          localStorage.removeItem(this.authLocalStorageToken);
          
          // Start countdown before redirecting to login
          this.startRedirectCountdown(5, '/auth');
        },
        error: (error) => {
          console.log('Password change error:', error);
          this.isLoading = false; // Re-enable button on error ONLY
          this.clearRedirectCountdown(); // Stop any ongoing countdown
          this.showError = true;
          this.showSuccess = false; // Hide success message
          this.errorMessage = error.error || 'An error occurred. Please try again.';
          this.toastr.error(this.errorMessage, 'Error!');
          
          // Increment attempt counter - will auto-redirect after 3rd attempt
          this.authSessionService.incrementPasswordAttempt();
        }
      });
    }
  };

  ngOnDestroy(): void {
    // Clean up any ongoing countdown when component is destroyed
    this.clearRedirectCountdown();
  }
  }
